import React from 'react';
import { useNavigate } from 'react-router-dom';

const StudentsTable = ({
    students,
    loading,
    actionLoading = {},
    hasFetchedRef,
    totalStudents,
    branchCounts = {},
    currentPage,
    isAnyFilterApplied,
    onClearFilters,
    onMapStudent,
    onUnmapStudent
}) => {
    const navigate = useNavigate();

    // Helper function to get degree information
    const getDegreeInfo = (student) => {
        const degrees = [];

        if (student.diplomaDegree && student.diplomaDegree.trim() !== '') {
            degrees.push(`${student.diplomaDegree} - ${student.diplomaDepartment || ''}`);
        }

        if (student.ugDegree && student.ugDegree.trim() !== '') {
            degrees.push(`${student.ugDegree} - ${student.ugDepartment || ''}`);
        }

        if (student.pgDegree && student.pgDegree.trim() !== '') {
            degrees.push(`${student.pgDegree} - ${student.pgDepartment || ''}`);
        }

        if (student.qualification && student.qualification.trim() !== '') {
            return student.qualification;
        }

        return degrees.length > 0 ? degrees.join(', ') : '';
    };

    // Helper function to get passed out years
    const getPassedOutYears = (student) => {
        const years = [];

        if (student.diplomaPassout) {
            years.push(`Diploma: ${student.diplomaPassout}`);
        }

        if (student.ugPassout) {
            years.push(`UG: ${student.ugPassout}`);
        }

        if (student.pgPassout) {
            years.push(`PG: ${student.pgPassout}`);
        }

        if (years.length === 0 && student.passedOutYear) {
            return student.passedOutYear;
        }

        return years.length > 0 ? years.join(', ') : '';
    };

    // Helper function to truncate text
    const truncateText = (text, maxLength = 20) => {
        if (!text || text === '') return text;
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Helper function to get experience
    const getExperienceInfo = (student) => {
        if (!student.experience || student.experience.trim() === "AN?") return "";
        return `Experience: ${student.experience}`;
    };

    // Sub-components
    const SkillsCell = ({ student }) => {
        const matchedSkills = student.matchedSkills || [];
        const displaySkills = matchedSkills.slice(0, 2);
        const remainingCount = matchedSkills.length - 2;
        const allSkillsText = matchedSkills.join(', ');

        return (
            <div className="skills-cell" title={allSkillsText}>
                {matchedSkills.length > 0 ? (
                    <div className="matched-skills-container">
                        {displaySkills.map((skill, index) => (
                            <span key={index} className="matched-skill-tag">
                                {truncateText(skill, 10)}
                            </span>
                        ))}
                        {remainingCount > 0 && (
                            <span className="more-skills-badge">
                                +{remainingCount}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="no-skills">—</span>
                )}
            </div>
        );
    };

    const ActionButtons = ({ isMapped, studentId, mappingId, isActionLoading }) => {
        if (isMapped) {
            return (
                <button
                    onClick={() => onUnmapStudent(studentId, mappingId)}
                    className="action-btn unmap-btn"
                    disabled={isActionLoading}
                    title="Remove mapping"
                >
                    {isActionLoading ? (
                        <span className="btn-spinner"></span>
                    ) : (
                        <>
                            <span className="btn-icon">×</span>
                            Unmap
                        </>
                    )}
                </button>
            );
        }

        return (
            <button
                onClick={() => onMapStudent(studentId)}
                className="action-btn map-btn"
                disabled={isActionLoading}
                title="Map student to this job"
            >
                {isActionLoading ? (
                    <span className="btn-spinner"></span>
                ) : (
                    <>
                        <span className="btn-icon">+</span>
                        Map
                    </>
                )}
            </button>
        );
    };

    const StudentRow = ({ student, isActionLoading }) => {
        const degreeInfo = getDegreeInfo(student);
        const yearInfo = getPassedOutYears(student);
        const experienceInfo = getExperienceInfo(student);
        const dbId = student.id;                 // numeric id for edit page
        const studentCode = student.studentId;   // real studentId for mapping
        const handleStudentClick = (studentId) => {
            // Store current tab in sessionStorage before navigating
            const currentPath = window.location.pathname;
            sessionStorage.setItem('lastJobReqPath', currentPath);
            sessionStorage.setItem('lastActiveTab', 'matchingStudents');

            navigate(`/edits-student/${studentId}`);
        };
        return (
            <tr
                className={
                    student.placementInterest === "Critical"
                        ? "critical-row"
                        : student.ProgressStatus === "Course Completed"
                            ? "completed-row"
                            : ""
                }
                title={
                    student.placementInterest === "Critical"
                        ? student.staffRemarks || "No remarks"
                        : ""
                }
            >
                {/* NAME + BRANCH */}
                <td className="stack-cell">
                    <span
                        className="student-name-link"
                        onClick={() => handleStudentClick(dbId)}
                    >
                        {student.studentName || ""}
                    </span>
                    <div className="sub-text">
                        {student.branch || ""}
                        {student.studentContactNumber && (
                            <>,No.{student.studentContactNumber}</>
                        )}
                    </div>
                </td>

                {/* DEGREE + YEAR */}
                <td
                    className="stack-cell"
                    title={`${degreeInfo} | ${yearInfo} ${experienceInfo ? '| ' + experienceInfo : ''}`}
                >
                    <div className="primary-text">{degreeInfo}</div>
                    <div className="sub-text">{yearInfo}</div>
                    {experienceInfo && (
                        <div className="sub-text experience-text">
                            {experienceInfo}
                        </div>
                    )}
                </td>

                {/* COURSE NAME */}
                <td
                    className="truncate-cell"
                    title={student.courseName || ""}>
                    {student.courseName || ""}</td>

                {/* DESIRED LOCATION */}
                <td
                    className="truncate-cell"
                    title={student.desiredlocation || ""}
                >
                    {student.desiredlocation || ""}</td>

                {/* SKILLS */}
                <td>
                    <SkillsCell student={student} />
                </td>

                {/* MATCH % */}
                <td className="match-percent">
                    {student.matchScore || 0}%
                </td>

                {/* ACTION */}
                <td className="actions-cell">
                    <ActionButtons
                        isMapped={student.isMapped}
                        studentId={studentCode}
                        mappingId={student.mappingId}
                        isActionLoading={isActionLoading}
                    />
                </td>
            </tr>
        );
    };

    return (
        <div className="students-table-section">
            <div className="section-header">
                <div className="title-row">
                    <h3 className="section-title">
                        Matching Students
                    </h3>

                    <span className="student-count-badge">
                        {loading ? '...' : totalStudents} {totalStudents === 1 ? 'Student' : 'Students'}
                    </span>
                </div>

                {!loading && Object.keys(branchCounts).length > 0 && (
                    <div className="branch-counts">
                        {Object.entries(branchCounts).map(([branch, count]) => (
                            <span key={branch} className="branch-count-badge">
                                {branch}: {count}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {loading && hasFetchedRef.current ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading students...</p>
                </div>
            ) : students.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <p className="empty-message">No matching students found</p>
                    {isAnyFilterApplied && (
                        <button
                            onClick={onClearFilters}
                            className="clear-filters-btn"
                        >
                            Clear All Filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="jobtable-wrapper">
                    <table className="students-table">
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Course</th>
                                <th title="Preferred Job Location">Preferred</th>
                                <th>Skills</th>
                                <th>Match</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {students.map((student) => {
                                const studentId = student.id || student.studentId;
                                return (
                                    <StudentRow
                                        key={studentId}
                                        student={student}
                                        isActionLoading={actionLoading[studentId] || false}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default StudentsTable;